<template>
  <div class="activity-stage starts-with">
    <div class="steps-container">
      <figure class="step-image">
        <img v-if="currentStep.imagePath" :src="currentStep.imagePath" />
      </figure>
      <text-display-list :text-size="textSize" class="text-steps">
        <li
          v-for="step in currentRound.steps"
          :key="`${currentRound.roundIndex}-${step.stepIndex}`"
          :class="`step step-${step.stepIndex}`"
        >
          <color-block-text :text="step.answer" :text-step="step.stepIndex"> </color-block-text>
        </li>
      </text-display-list>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import find from 'lodash/find';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import { getCharacterAmountInSteps } from 'utils/activity-util';

import ColorBlockText from 'components/text-displays/ColorBlockText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ColorBlockText,
    TextDisplayList
  },
  mixins: [TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'StartsWith'
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    textSize() {
      return getCharacterAmountInSteps(this.currentRound.steps || []) > 45
        ? 'medium'
        : 'medium-large';
    }
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      this.sfx = await this.loadAssets();
    },

    async loadImages() {
      this.rounds.forEach(round => {
        round.steps.forEach(step => this.queueImage(step.imagePath));
      });

      return this.downloadImages();
    },

    async loadAssets() {
      const resolutions = await Promise.all([
        this.loadImages(),
        this.getActivityRoundSounds(this.rounds, this.spokenWordsPrefix)
      ]);

      return find(resolutions, el => el.soundsPerRound);
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type each word using the starting combo.',
          voice: `${this.voiceInstructionsPrefix}/startswith-${this.getRandomVoice()}-1.mp3`,
          delay: 4
        }
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.hideAllSteps();
        this.fadeInStep(this.currentStep.stepIndex);
      });
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentWord) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.playStepSound(this.sfx);
      this.finishStep();
      this.fadeInStep(this.currentStep.stepIndex);
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .to('.steps-container', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [
            () =>
              timeline
                .set('.step', { opacity: 0 })
                .set(`.step-${this.currentStep.stepIndex}`, { opacity: 1 })
                .to('.steps-container', 0.5, { opacity: 1 })
          ]);
      }
    },

    fadeInStep(stepIndex) {
      TweenMax.to(`.step-${stepIndex}`, 0.25, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.step', { opacity: 0 });
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-stage {
  ul {
    list-style-type: none;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    padding: 20px;
    margin: 0;
  }

  p {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    font-family: hwtprint;
  }

  .step-image {
    height: 270px;
    text-align: center;
  }
}
</style>
